import { z } from "zod";
const hex = z.string().regex(/^#[0-9a-fA-F]{6}$/, "colour must be #RRGGBB");
/** Validation for project settings edited in the UI, over the API, or by an agent. */
export const projectConfigSchema = z.object({
    startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "use YYYY-MM-DD"),
    workdaysPerWeek: z.number().int().min(1).max(7),
    runwayWeeks: z.number().int().min(1).max(520),
    milestoneWeeks: z.number().int().min(1).max(52),
    defaultUtilization: z.number().min(0.05).max(1),
    splitThresholdPd: z.number().positive(),
    disciplines: z.array(z.object({
        key: z.string().min(1).regex(/^[a-z0-9_]+$/, "lowercase letters, digits and _"),
        label: z.string().min(1),
        hintKeywords: z.array(z.string().min(1)).optional(),
        icon: z.string().min(1).max(40).optional(),
        color: hex.optional(),
        outsourced: z.boolean().optional(),
    })).min(1),
    resources: z.array(z.object({
        key: z.string().min(1),
        name: z.string().min(1),
        firstName: z.string().max(60).optional(),
        lastName: z.string().max(60).optional(),
        nickname: z.string().max(40).optional(),
        discipline: z.string().min(1),
        extraDisciplines: z.array(z.string().min(1)).optional(),
        fte: z.number().positive().max(1),
        defaultUtilization: z.number().min(0.05).max(1).optional(),
        offsetWeeks: z.number().min(0).optional(),
        blockLabel: z.string().optional(),
        userId: z.string().min(1).optional(),
    })),
    teams: z.array(z.object({
        id: z.number().int().min(0),
        name: z.string().min(1),
        color: hex,
        members: z.array(z.string()),
        milestonesFromExecutors: z.boolean().optional(),
    })).min(1),
    homeTeam: z.record(z.string(), z.number().int()),
    workstreamOwners: z.record(z.string(), z.number().int()),
    workstreamOwnerRules: z.array(z.object({ pattern: z.string().min(1), team: z.number().int() })),
    defaultOwnerTeam: z.number().int(),
    priorities: z.array(z.object({
        key: z.string().min(1),
        label: z.string().min(1),
        color: hex,
        tier: z.number().int().min(0),
        rank: z.number().int().min(0),
        excluded: z.boolean().optional(),
    })).min(1),
    nameAliases: z.array(z.tuple([z.string().min(1), z.string().min(1)])),
    display: z.object({ personName: z.enum(["first", "last"]).optional() }).optional(),
}).superRefine((c, ctx) => {
    const disc = new Set(c.disciplines.map(d => d.key));
    const res = new Set();
    c.resources.forEach((r, i) => {
        if (res.has(r.key))
            ctx.addIssue({ code: "custom", path: ["resources", i, "key"], message: `duplicate person key "${r.key}"` });
        res.add(r.key);
        if (!disc.has(r.discipline))
            ctx.addIssue({ code: "custom", path: ["resources", i, "discipline"], message: `unknown discipline "${r.discipline}"` });
        (r.extraDisciplines || []).forEach((d, j) => {
            if (!disc.has(d))
                ctx.addIssue({ code: "custom", path: ["resources", i, "extraDisciplines", j], message: `unknown discipline "${d}"` });
        });
    });
    const teams = new Set(c.teams.map(t => t.id));
    c.teams.forEach((t, i) => t.members.forEach((m, j) => {
        if (!res.has(m))
            ctx.addIssue({ code: "custom", path: ["teams", i, "members", j], message: `unknown person "${m}"` });
    }));
    const teamRef = (id, path) => {
        if (!teams.has(id))
            ctx.addIssue({ code: "custom", path, message: `unknown team id ${id}` });
    };
    teamRef(c.defaultOwnerTeam, ["defaultOwnerTeam"]);
    Object.entries(c.workstreamOwners).forEach(([w, id]) => teamRef(id, ["workstreamOwners", w]));
    Object.entries(c.homeTeam).forEach(([p, id]) => teamRef(id, ["homeTeam", p]));
    c.workstreamOwnerRules.forEach((r, i) => {
        teamRef(r.team, ["workstreamOwnerRules", i, "team"]);
        try {
            new RegExp(r.pattern, "i");
        }
        catch {
            ctx.addIssue({ code: "custom", path: ["workstreamOwnerRules", i, "pattern"], message: "invalid regular expression" });
        }
    });
    c.nameAliases.forEach(([, p], i) => {
        if (!res.has(p))
            ctx.addIssue({ code: "custom", path: ["nameAliases", i, 1], message: `unknown person "${p}"` });
    });
});
/** A minimal starting configuration for a brand-new project. */
export function blankProjectConfig(startDate) {
    return {
        startDate,
        workdaysPerWeek: 5,
        runwayWeeks: 26,
        milestoneWeeks: 4,
        defaultUtilization: 0.8,
        splitThresholdPd: 16,
        disciplines: [
            { key: "eng", label: "Engineering", hintKeywords: ["eng"], icon: "code", color: "#4C9AF5" },
            { key: "des", label: "Design", hintKeywords: ["des"], icon: "dice", color: "#C4D93C" },
        ],
        resources: [],
        teams: [{ id: 0, name: "TEAM", color: "#3D77B0", members: [] }],
        homeTeam: {},
        workstreamOwners: {},
        workstreamOwnerRules: [],
        defaultOwnerTeam: 0,
        priorities: [
            { key: "P0", label: "Blocker", color: "#D04437", tier: 0, rank: 0 },
            { key: "P1", label: "Critical", color: "#E3492F", tier: 0, rank: 1 },
            { key: "P2", label: "Major", color: "#EA7D24", tier: 1, rank: 2 },
            { key: "P3", label: "Minor", color: "#57A55A", tier: 2, rank: 3 },
            { key: "P4", label: "Trivial", color: "#8993A4", tier: 3, rank: 4, excluded: true },
        ],
        nameAliases: [],
    };
}
//# sourceMappingURL=config.js.map